import { useRef } from 'react';
import type { ProjectEntry } from '../content/types';
import { predecode } from '../lib/decode';
import { useDragScroll } from '../lib/useDragScroll';
import { Arrow, ProjectLogo, StatusBadge } from '../ui/brand';
import { useContent } from '../ui/ContentContext';
import { defaultTab, tabItems } from './Gallery';
import { SmartImg } from '../ui/SmartImg';

/** 01 · Selección de proyecto: héroe azul + grilla de 2 columnas */
export function Home({ onOpen, onAdmin }: { onOpen: (p: ProjectEntry) => void; onAdmin: () => void }) {
  const { content } = useContent();
  const { manifest, src } = content;
  const scroller = useRef<HTMLDivElement>(null);
  useDragScroll(scroller, 'y');

  // Al apoyar el dedo (antes del click y de la transición de 220 ms) se adelanta la decodificación
  const warm = (p: ProjectEntry) => {
    const list = tabItems(p, defaultTab(p));
    predecode([src(list[0]?.main), ...list.slice(0, 6).map((r) => src(r.thumb)), src(list[1]?.main)], true);
  };

  return (
    <div className="screen flex flex-col" data-screen="proyectos">
      <div className="relative flex-none overflow-hidden" style={{ height: 560, background: 'linear-gradient(180deg, #1C294C 0%, #18233F 100%)' }}>
        <HeroFx />
        <div className="relative flex h-full flex-col justify-between" style={{ padding: '80px 64px 64px' }}>
          <LongPress ms={3000} onLongPress={onAdmin}>
            <img src="/brand/logo-ontario-blanco.png" alt="Ontario" className="a-fade block" style={{ height: 112, width: 'auto' }} draggable={false} />
          </LongPress>
          <div className="pointer-events-none flex flex-col" style={{ gap: 22 }}>
            <div className="rounded bg-secundario" style={{ width: 120, height: 8, animation: 'tkLeft 600ms 150ms var(--ease-out) both' }} />
            <div className="font-bold text-blanco" style={{ fontSize: 80, lineHeight: 1.05, letterSpacing: -1, animation: 'tkUp 650ms 220ms var(--ease-out) both' }}>
              Nuestros proyectos
            </div>
            <div className="flex items-center text-blanco/80" style={{ gap: 16, fontSize: 28, animation: 'tkUp 650ms 340ms var(--ease-out) both' }}>
              <div className="rounded-full bg-secundario" style={{ width: 14, height: 14, animation: 'fxBlink 2s ease-in-out infinite' }} />
              <div>Toca un proyecto para ver sus imágenes</div>
            </div>
          </div>
        </div>
      </div>

      <div ref={scroller} className="noscroll flex-1 overflow-y-auto" style={{ padding: '48px 64px 64px', touchAction: 'pan-y' }}>
        <div className="grid grid-cols-2" style={{ gap: 32 }}>
          {manifest.projects.map((p, n) => (
            <button
              key={p.id}
              type="button"
              className="pressable relative flex flex-col overflow-hidden rounded-card bg-blanco text-left"
              style={{ height: 620, animation: `tkUp 560ms ${120 + n * 70}ms var(--ease-out) both` }}
              onPointerDown={() => warm(p)}
              onClick={() => onOpen(p)}
            >
              <div className="relative w-full flex-none" style={{ height: 420 }}>
                <SmartImg file={p.cover.card} lqip={p.cover.lqip} className="fill" alt={p.nombre} />
                <div className="absolute" style={{ top: 28, left: 28 }}>
                  <StatusBadge estado={p.estado} />
                </div>
              </div>
              <div className="flex w-full flex-1 items-center justify-between" style={{ padding: '0 28px 0 32px', gap: 20 }}>
                <div className="flex min-w-0 flex-1 items-center" style={{ height: 124 }}>
                  <ProjectLogo p={p} k={1} />
                </div>
                <div className="flex flex-none items-center justify-center rounded-full bg-secundario text-primario" style={{ width: 72, height: 72 }}>
                  <Arrow dir="right" size={34} />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Efectos del héroe del diseño, reducidos a animaciones CSS de transform/opacity (sin JS por frame) */
function HeroFx() {
  const ring = (lime: boolean) =>
    lime
      ? { border: '3px solid #E6F06A', boxShadow: '0 0 0 6px rgba(198,211,0,0.25)' }
      : { border: '2px solid #FFFFFF', boxShadow: '0 0 0 6px rgba(160,190,255,0.2)' };
  const shapes = [
    { left: 120, top: 70, size: 96, look: ring(true), anim: 'fxFloatA 5s ease-in-out infinite alternate' },
    { left: 470, top: 40, size: 78, look: ring(false), anim: 'fxFloatB 6s ease-in-out infinite alternate' },
    { left: 860, top: 120, size: 104, look: { background: '#C6D300', boxShadow: '0 0 0 8px rgba(198,211,0,0.2)' }, anim: 'fxFloatC 5.5s ease-in-out infinite alternate' },
    { left: 640, top: 330, size: 84, look: ring(true), anim: 'fxFloatA 6.5s ease-in-out infinite alternate' },
    { left: 940, top: 420, size: 70, look: ring(false), anim: 'fxFloatB 7s ease-in-out infinite alternate' },
  ];
  const sparks = [[300, 30], [700, 40], [1030, 250], [520, 270], [180, 300], [880, 500], [40, 500], [620, 510]];
  return (
    <div className="fill pointer-events-none" style={{ opacity: 0.39 }}>
      <div className="absolute" style={{ left: 640, top: -120, width: 560, height: 560, background: 'radial-gradient(closest-side, rgba(198,211,0,0.22) 0%, rgba(28,41,76,0) 100%)', animation: 'fxPulse 6s ease-in-out infinite' }} />
      <div className="absolute" style={{ left: -120, top: 240, width: 520, height: 520, background: 'radial-gradient(closest-side, rgba(140,170,255,0.18) 0%, rgba(28,41,76,0) 100%)', animation: 'fxPulse 7s 2s ease-in-out infinite' }} />
      {shapes.map((s, i) => (
        <div key={i} className="absolute rounded-full" style={{ left: s.left, top: s.top, width: s.size, height: s.size, animation: s.anim, ...s.look }} />
      ))}
      {sparks.map(([left, top], k) => {
        const size = 24 + (k % 3) * 10;
        return (
          <div key={k} className="absolute" style={{ left, top, width: size, height: size, animation: `fxTwinkle ${3.2 + (k % 4) * 0.5}s ${k * 0.55}s ease-in-out infinite` }}>
            <div className="absolute rounded" style={{ left: 0, top: size / 2 - 1.5, width: size, height: 3, background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, #FFFFFF 50%, rgba(255,255,255,0) 100%)' }} />
            <div className="absolute rounded" style={{ top: 0, left: size / 2 - 1.5, width: 3, height: size, background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, #FFFFFF 50%, rgba(255,255,255,0) 100%)' }} />
            <div className="absolute rounded-full" style={{ left: size / 2 - 4, top: size / 2 - 4, width: 8, height: 8, background: '#F4FAB0' }} />
          </div>
        );
      })}
    </div>
  );
}

/** Pulsación larga (acceso oculto al panel técnico) */
function LongPress({ ms, onLongPress, children }: { ms: number; onLongPress: () => void; children: React.ReactNode }) {
  const t = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = () => {
    if (t.current) clearTimeout(t.current);
    t.current = null;
  };
  return (
    <div
      className="self-start"
      onPointerDown={() => {
        clear();
        t.current = setTimeout(onLongPress, ms);
      }}
      onPointerUp={clear}
      onPointerLeave={clear}
      onPointerCancel={clear}
      onContextMenu={(e) => e.preventDefault()}
    >
      {children}
    </div>
  );
}
